import { Select } from 'antd-v5'
import React, { useEffect, useState } from 'react'
import makeCancellablePromise from "make-cancellable-promise";
import _, { noop } from "lodash";
import { parseSelectOptions  as globalParseSelectOptions } from 'utils/formUtils';
import { isEmpty } from 'utils/lodashUtils';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import LoadingIcon from 'ac-components/ac-icons/LoadingIcon';

/**
 * This component is just a wrapper to antd select.
 * This will not allow the Select component to show raw values if it is in loading state,
 * which means data is not yet available for the list, so showing value will make no sense.
 */
export default function CustomSelect({ api, apiParams, dependency, parseSelectOptions, getFormListOptions, listField, propsGenerator, showValueOnload = false, dropdownRender = null, popupMatchSelectWidth = false, optionFilterProp = "label", loadWhenInView = false, notFoundContent, ...props}) {
    const [options, setOptions] = useState(props.options);
    const [isInView, setIsInView] = useState(!loadWhenInView);
    const { inView: inViewport, ref } = useInView();
    const apiParamsDependency = props.detechAPIParamChange ? JSON.stringify(apiParams) : apiParams;

    useEffect(() => {
        if (inViewport) {
            setIsInView(true);
        }
    }, [inViewport]);

    useEffect(() => {
        if(getFormListOptions && !isEmpty(listField)){
            const { name } = listField;
            const _options = getFormListOptions(name);
            setOptions(_options);
        }
    }, [listField, getFormListOptions]);

    useEffect(() => {
        //when loadWhenInView prop is true
        //only call the API when isInView is true
        if(_.isFunction(api) && (loadWhenInView ? isInView : true)){
            setOptions();
            const apiRequest = api(apiParams);
            let cancel, promise;
            if(apiRequest instanceof Promise) {
                promise = apiRequest;
                cancel = makeCancellablePromise(promise).cancel;
            } else {
                promise = apiRequest.promise;
                cancel = apiRequest.cancel;
            }
            promise.then(parseSelectOptions || globalParseSelectOptions).then(setOptions).catch(e => console.error(e));
            return cancel;
        }
    }, [api, apiParamsDependency, parseSelectOptions, dependency, isInView, loadWhenInView]);

    useEffect(() => {
        if(!api && !getFormListOptions){
            setOptions(props.options);
        }
    }, [props.options, api, getFormListOptions]);

    const { optionsMutator = defaultOptionsMutator} = props;
    const mutatedOptions = optionsMutator(options ?? []);
    const _props = {
        ..._.omit(props, ["api", "apiParams", "optionsMutator"]),
        ...((propsGenerator && !isEmpty(listField)) ? propsGenerator(listField.name) : {}),
    };

    const loading = _props.loading || (_.isFunction(api) && _.isUndefined(options));

    const selectComponent = (
        <Select
          {..._props}
          className={classNames(_props.className, "custom-select")}
          onChange={(value, option) =>
            props.onChange ? props.onChange(value, listField, option) : noop()
          }
          allowClear={props.allowClear && !_.isNull(props.value)}
          value={
            (!showValueOnload && loading) || _.isNull(_props.value)
              ? undefined
              : _props.value
          }
          options={mutatedOptions}
          popupMatchSelectWidth={popupMatchSelectWidth}
          loading={loading}
          dropdownRender={dropdownRender}
          optionFilterProp={optionFilterProp}
          notFoundContent={loading ? <div className='text-center'><LoadingIcon /></div> : notFoundContent}
        />
      );
    
    return loadWhenInView ? (
        <div ref={ref}>{selectComponent}</div>
      ) : (
        selectComponent
      );
}

function defaultOptionsMutator(list = []){
    return list;
}